import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus } from "lucide-react";

const newMedRow = () => ({
  _key: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `m-${Date.now()}-${Math.random()}`,
  name: "", dosage: "", duration: "",
});
const emptyRx = () => ({ patient_id: "", diagnosis: "", medicines: [newMedRow()], instructions: "", follow_up_date: "" });

export const PrescriptionsTab = ({ users }) => {
  const [rx, setRx] = useState(emptyRx);
  const [open, setOpen] = useState(false);

  const addMed = () => setRx({ ...rx, medicines: [...rx.medicines, newMedRow()] });
  const setMed = (i, k, v) => setRx({ ...rx, medicines: rx.medicines.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)) });

  const save = async () => {
    try {
      await api.post("/prescriptions", rx);
      toast.success("Prescription issued");
      setRx(emptyRx()); setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button className="bg-primary text-primary-foreground btn-pill mb-4" data-testid="adm-rx-add">
            <Plus size={14} className="mr-1"/> Issue Prescription
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">New Prescription</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Patient</Label>
              <Select value={rx.patient_id} onValueChange={(v) => setRx({ ...rx, patient_id: v })}>
                <SelectTrigger data-testid="rx-patient"><SelectValue placeholder="Select patient"/></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Diagnosis</Label><Input value={rx.diagnosis} onChange={(e)=>setRx({...rx,diagnosis:e.target.value})} data-testid="rx-diagnosis"/></div>
            <div>
              <Label>Medicines</Label>
              {rx.medicines.map((m, i) => (
                <div key={m._key || `med-${i}`} className="grid grid-cols-3 gap-2 mb-2">
                  <Input placeholder="Name" value={m.name} onChange={(e)=>setMed(i,"name",e.target.value)} data-testid={`rx-med-name-${i}`}/>
                  <Input placeholder="Dosage" value={m.dosage} onChange={(e)=>setMed(i,"dosage",e.target.value)} data-testid={`rx-med-dosage-${i}`}/>
                  <Input placeholder="Duration" value={m.duration} onChange={(e)=>setMed(i,"duration",e.target.value)} data-testid={`rx-med-duration-${i}`}/>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addMed} data-testid="rx-add-med"><Plus size={12} className="mr-1"/>Add Medicine</Button>
            </div>
            <div><Label>Instructions</Label><Textarea rows={3} value={rx.instructions} onChange={(e)=>setRx({...rx,instructions:e.target.value})} data-testid="rx-instructions"/></div>
            <div><Label>Follow-up date</Label><Input type="date" value={rx.follow_up_date} onChange={(e)=>setRx({...rx,follow_up_date:e.target.value})} data-testid="rx-followup"/></div>
          </div>
          <Button onClick={save} className="bg-primary text-primary-foreground mt-4" data-testid="rx-save">Issue Prescription</Button>
        </DialogContent>
      </Dialog>
      <p className="text-sm text-muted-foreground">Issued prescriptions appear in each patient&apos;s dashboard.</p>
    </>
  );
};

export default PrescriptionsTab;
